import { Box, Checkbox, Typography } from '@mui/material'
import { TooltipText } from 'components/TooltipText/TooltipText'
import { format } from 'date-fns'
import i18n from 'lib/i18'
import { contentLanguages } from 'lib/languages'
import theme from 'lib/theme'
import { useLocale } from 'providers/LocaleProvider'
import { Dispatch, MouseEvent, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { Order, OrderPart } from 'types/api/order'
import { ASAP_SLOT } from 'types/model/delivery'
import { LabelsCount, LabelsWrapper } from './Orders.styled'

export const PartIdColumn = ({ order }: { order: Order }) => {
  return (
    <>
      {order.parts.map((part) => {
        const labelsCount = part.labels?.length || 0
        const labels = part.labels?.map((label) => {
          if (typeof label === 'string') {
            return label
          }
          return label[contentLanguages[0]] || label[i18n.language]
        })
        const label = labels?.length > 0 ? labels[0] : ''
        return (
          <Box minHeight='48px' key={part.visibleId + part.id} display='flex'>
            <Box>
              <Typography variant='body2'>{part.id}</Typography>
            </Box>
            {label && (
              <Box ml={1}>
                <LabelsWrapper backgroundColor={theme.palette.icon.hover}>
                  <Typography
                    color={theme.palette.commonColors.white}
                    variant='body2'>
                    {label}
                  </Typography>
                </LabelsWrapper>
              </Box>
            )}
            {labelsCount - 1 > 0 && (
              <TooltipText
                height={theme.spacing(3)}
                text={
                  <>
                    {labels.slice(1).map((label) => (
                      <Typography variant='body2' key={label}>
                        {label}
                      </Typography>
                    ))}
                  </>
                }>
                <LabelsCount backgroundColor={theme.palette.icon.hover}>
                  <Typography
                    color={theme.palette.commonColors.white}
                    variant='body2'
                    sx={{ fontSize: '10px' }}>
                    +{labelsCount - 1}
                  </Typography>
                </LabelsCount>
              </TooltipText>
            )}
          </Box>
        )
      })}
    </>
  )
}

export const CheckboxColumn = ({
  checkedOrders,
  order,
  setCheckedOrders,
}: {
  order: Order
  checkedOrders: { id: string; visibleId: string }[]
  setCheckedOrders: Dispatch<
    SetStateAction<{ id: string; visibleId: string }[]>
  >
}) => {
  const { t } = useTranslation()
  const handleCheckboxClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (checkedOrders.find((id) => id.id === order.id)) {
      setCheckedOrders(() => checkedOrders.filter((id) => id.id !== order.id))
    } else {
      setCheckedOrders(() => {
        return checkedOrders.length > 0
          ? [...checkedOrders, { id: order.id, visibleId: order.visibleId }]
          : [{ id: order.id, visibleId: order.visibleId }]
      })
    }
  }
  return (
    <Checkbox
      inputProps={{ 'aria-label': t('selectOrderForCancellation') }}
      sx={{
        padding: theme.spacing(1),
        '&.Mui-checked .MuiSvgIcon-root': {
          fill: theme.palette.primary.main,
        },
      }}
      checked={Boolean(checkedOrders.find((o) => o.id === order.id))}
      onClick={(event) => handleCheckboxClick(event)}
    />
  )
}

export const FulfilmentColumn = ({ part }: { part: OrderPart }) => {
  const { t } = useTranslation()
  const { selectedLocale } = useLocale()
  const deliverySlot = part.deliveryMethod?.service?.deliverySlot
  // deliveryDate sometimes has the time included in time string
  const date = format(
    new Date(deliverySlot.deliveryDate.substring(0, 10)),
    'P',
    { locale: selectedLocale }
  )
  const asapSlot =
    part.deliveryMethod?.service?.deliverySlot?.attributes?.includes(ASAP_SLOT)
  const formattedDeliverySlot = `${date} ${deliverySlot.startTime}-${deliverySlot.endTime}`
  return (
    <Box minHeight='48px' key={part.id + part.visibleId}>
      <Typography>
        {t(`fulfilmentStatus.${part.deliveryMethod?.service?.type}`)}
      </Typography>
      <Typography sx={{ color: theme.palette.text.secondary }}>
        {formattedDeliverySlot}
      </Typography>
      {asapSlot && (
        <Typography sx={{ color: theme.palette.text.secondary }}>
          {t('ASAP')}
        </Typography>
      )}
    </Box>
  )
}
